import PropTypes from 'prop-types';

import { Carousel } from '@/design-system/atoms/Carousel';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { Text } from '@/design-system/atoms/Text';
import { Title } from '@/design-system/atoms/Title';

import styles from './PhysicianEducation.module.scss';

const PhysicianEducation = ({
    title = 'Education & Experience',
    trainingTitle = 'Training',
    boardTitle = 'Board Certifications',
    educations,
    certifications,
}) => {
    const CarouselItems = certifications?.map((certification, index) => (
        <Carousel.Card key={index} className={styles['physician-education__certification']}>
            <div className={styles['physician-education__certification-content']}>
                <Text
                    className={styles['physician-education__certification-name']}
                    content={certification.certification}
                    variant={Text.VARIANT.T2B}
                    tag={Text.TAG.P}
                />
                <Text
                    className={`${styles['physician-education__certification-organization']} GlobalLinkNoTx`}
                    content={certification.organization}
                    variant={Text.VARIANT.T2}
                    tag={Text.TAG.P}
                />
            </div>
        </Carousel.Card>
    ));

    return (
        <ContentContainer className={styles['physician-education']}>
            <Title
                className={styles['physician-education__title']}
                content={title}
                variant={Title.VARIANT.H3}
                tag={Title.TAG.H2}
            />
            {Array.isArray(educations) && educations.length > 0 && (
                <>
                    <Text
                        className={styles['physician-education__eyebrow']}
                        content={trainingTitle}
                        variant={Text.VARIANT.E1}
                        tag={Text.TAG.P}
                    />
                    <Grid
                        className={styles['physician-education__education']}
                        col={{ sm: 4, md: 6, lg: 12 }}
                    >
                        {educations.map((education, index) => (
                            <GridColumn
                                key={index}
                                colSpan={{ sm: 4, md: 3, lg: 3 }}
                                className={styles['physician-education__education-item']}
                            >
                                <Text
                                    className={`${styles['physician-education__education-type']} GlobalLinkNoTx`}
                                    content={
                                        education.school !== null
                                            ? education.school
                                            : education.name
                                    }
                                    variant={Text.VARIANT.T2B}
                                    tag={Text.TAG.P}
                                />
                                <Text
                                    className={styles['physician-education__education-name']}
                                    content={education.type}
                                    variant={Text.VARIANT.T2}
                                    tag={Text.TAG.P}
                                />
                            </GridColumn>
                        ))}
                    </Grid>
                </>
            )}
            {Array.isArray(certifications) && certifications.length > 0 && (
                <>
                    <Text
                        className={styles['physician-education__eyebrow']}
                        content={boardTitle}
                        variant={Text.VARIANT.E1}
                        tag={Text.TAG.P}
                    />
                    <Carousel.Root>
                        <Carousel.Content
                            items={CarouselItems}
                            className={styles['physician-education__certifications']}
                        />
                        <Carousel.Footer>
                            <Carousel.Spacer />
                            <Carousel.Navigation
                                analytics={{
                                    componentName: 'fad_bio_education_experience',
                                    componentTitle: title,
                                }}
                            />
                        </Carousel.Footer>
                    </Carousel.Root>
                </>
            )}
        </ContentContainer>
    );
};

PhysicianEducation.propTypes = {
    /**
     * Title of the component
     */
    title: PropTypes.string,
    /**
     * Title for the education section
     */
    trainingTitle: PropTypes.string,
    /**
     * Title for the certifications section
     */
    boardTitle: PropTypes.string,
    /**
     * Array of education items
     */
    educations: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * School name
             */
            school: PropTypes.string,
            /**
             * Education type
             */
            type: PropTypes.string,
            /**
             * Education name
             */
            name: PropTypes.string,
        }),
    ),
    /**
     * Array of certification items
     */
    certifications: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * Certification name
             */
            certification: PropTypes.string,
            /**
             * Certification organization
             */
            organization: PropTypes.string,
        }),
    ),
};

export default PhysicianEducation;
